import { useState } from "react";
import { Users, Sparkles, TrendingUp, ArrowRight, Mail, Phone, Building } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/hooks/useTenant";
import { toast } from "sonner";

interface Recommendation {
  contact_name: string;
  organization: string;
  recommendation_type: "referral" | "introduction" | "diligence" | "expertise";
  reasoning: string;
  confidence: "high" | "medium" | "low";
  suggested_action: string;
}

interface ReferralResult {
  recommendations: Recommendation[];
  summary: string;
}

export default function Referrals() {
  const { orgId, familyId } = useTenant();
  const [request, setRequest] = useState("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<ReferralResult | null>(null);

  const handleAnalyze = async () => {
    if (!request.trim()) {
      toast.error("Please enter a request description");
      return;
    }

    setIsAnalyzing(true);
    setResult(null);

    try {
      const { data, error } = await supabase.functions.invoke("referral-agent", {
        body: { request, orgId, familyId },
      });

      if (error) {
        if (error.message.includes("429")) {
          toast.error("Rate limit exceeded. Please try again in a moment.");
        } else if (error.message.includes("402")) {
          toast.error("AI credits depleted. Please add funds to continue.");
        } else {
          throw error;
        }
        return;
      }

      setResult(data);
      toast.success("Analysis complete!");
    } catch (err) {
      console.error("Referral analysis error:", err);
      toast.error(err instanceof Error ? err.message : "Failed to analyze request");
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "referral":
        return <ArrowRight className="h-4 w-4" />;
      case "introduction":
        return <Users className="h-4 w-4" />;
      case "diligence":
        return <TrendingUp className="h-4 w-4" />;
      case "expertise":
        return <Sparkles className="h-4 w-4" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "referral":
        return "bg-blue-500/10 text-blue-700 dark:text-blue-300";
      case "introduction":
        return "bg-green-500/10 text-green-700 dark:text-green-300";
      case "diligence":
        return "bg-purple-500/10 text-purple-700 dark:text-purple-300";
      case "expertise":
        return "bg-orange-500/10 text-orange-700 dark:text-orange-300";
      default:
        return "bg-gray-500/10 text-gray-700 dark:text-gray-300";
    }
  };

  const getConfidenceBadge = (confidence: string) => {
    const variants: Record<string, string> = {
      high: "bg-green-500/10 text-green-700 dark:text-green-300 border-green-500/20",
      medium: "bg-yellow-500/10 text-yellow-700 dark:text-yellow-300 border-yellow-500/20",
      low: "bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/20",
    };
    return (
      <Badge variant="outline" className={variants[confidence]}>
        {confidence} confidence
      </Badge>
    );
  };

  return (
    <div className="flex-1 space-y-6 px-6 py-6">

      <div>
        <h1 className="text-2xl font-bold tracking-tight">Referral Intelligence Agent</h1>
        <p className="text-muted-foreground">
          AI-powered contact recommendations based on your CRM data
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Input Card */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Your Request</CardTitle>
            <CardDescription>
              Describe the deal, opportunity, or request you need help with
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              placeholder="E.g., Looking for someone who can provide due diligence on a fintech startup in Southeast Asia, or need an introduction to a family office in London interested in sustainable investments..."
              value={request}
              onChange={(e) => setRequest(e.target.value)}
              rows={8}
              className="resize-none"
            />
            <Button
              onClick={handleAnalyze}
              disabled={isAnalyzing || !request.trim()}
              className="w-full"
            >
              {isAnalyzing ? (
                <>
                  <Sparkles className="h-4 w-4 animate-pulse" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  Analyze Request
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground">
              The agent will analyze your contacts, notes, and deals to find the best matches
            </p>
          </CardContent>
        </Card>

        {/* Results Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Recommendations</CardTitle>
            <CardDescription>
              AI-powered contact suggestions based on your network
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!result && !isAnalyzing && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  Enter your request to get personalized recommendations
                </p>
              </div>
            )}

            {isAnalyzing && (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <Sparkles className="h-12 w-12 text-primary animate-pulse mb-4" />
                <p className="text-muted-foreground">
                  Analyzing your network and CRM data...
                </p>
              </div>
            )}

            {result && (
              <div className="space-y-6">
                {/* Summary */}
                {result.summary && (
                  <div className="rounded-lg bg-muted/50 p-4 border">
                    <h3 className="font-semibold mb-2">Summary</h3>
                    <p className="text-sm text-muted-foreground">{result.summary}</p>
                  </div>
                )}

                {/* Recommendations */}
                <div className="space-y-4">
                  {result.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className="rounded-lg border p-4 space-y-3 hover:border-primary/50 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{rec.contact_name}</h3>
                            {getConfidenceBadge(rec.confidence)}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                            <Building className="h-3 w-3" />
                            {rec.organization}
                          </div>
                        </div>
                        <Badge className={getTypeColor(rec.recommendation_type)}>
                          <span className="flex items-center gap-1">
                            {getTypeIcon(rec.recommendation_type)}
                            {rec.recommendation_type}
                          </span>
                        </Badge>
                      </div>

                      <div className="space-y-2">
                        <div>
                          <p className="text-sm font-medium mb-1">Why this contact?</p>
                          <p className="text-sm text-muted-foreground">{rec.reasoning}</p>
                        </div>

                        <div>
                          <p className="text-sm font-medium mb-1">Suggested action</p>
                          <p className="text-sm text-muted-foreground">{rec.suggested_action}</p>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm">
                          <Mail className="h-3 w-3 mr-1" />
                          Email
                        </Button>
                        <Button variant="outline" size="sm">
                          <Phone className="h-3 w-3 mr-1" />
                          Call
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
